import React from 'react';
import classnames from 'classnames';
import {
  Avatar,
  Tooltip,
  Flex,
  Spinner,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody
} from '@chakra-ui/react';
import { PageButtons } from '@codametrix/ui-components';
import {
  AmplifyCore,
  caseUtils,
  commonEnums,
  cmxDateTime,
  IN_CQA,
  ASSIGNED_TO_ME,
  CQA_COMPLETE,
  CQA_APPROVED
} from '@codametrix/ui-common';
import { StatusDescription } from '../case-status/case-status';
import { Conditional } from '@codametrix/ui-components';
import { joinStrings } from '../../utils/utils';
import { MenuButton } from '../menu-button/menu-button';
import { MenuItem } from '../menu-button/menu-item';
import { CaseHold } from '../case-hold/case-hold';
import { CaseExclusion } from '../case-exclusion/case-exclusion';
import { Modal } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import pluralize from 'pluralize';
import { CaseComment } from '../case-comment/case-comment';
import { launchAmplifyCase } from '../case-detail/window-open-args';
import { ItemSelect } from './item-selector/item-select';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { CountdownTimer } from '../countdown-timer/countdown-timer';
import { useSelector } from 'react-redux';
import { isCQAWorkItemEvaluatedLegacy } from './CQAWorkItemEvaluatedLegacy';
import { applyCQAFilters } from '../../utils/activity-utils';
import { AuditActionBar } from './AuditActionBar';
import './styles/index.scss';
import UndoIcon from '@mui/icons-material/Undo';
import { RevertApproval } from './revert-approval/revert-approval';
const { conditionalUtils, benchEditable: benchEditableCore } = AmplifyCore;
const { getChildWindowParams } = caseUtils;

const {
  CapabilityAvailabilityReason,
  HoldReasonType,
  DocumentStatus,
  CursorOperation,
  AccelerateProcessStatus,
  BenchMode,
  CaseAlertType,
  CaseAccessState
} = commonEnums;

// Add isCaseAvailable function
const isCaseAvailable = (caseAccessState: string) => {  
  return (
    caseAccessState === CaseAccessState.NONE ||
    caseAccessState === CaseAccessState.ACQUIRED
  );
};

const DEFAULT_PROPS: Partial<Accelerate.CodeBenchActionBarPropsV2> = {
  processInstanceGuids: [],
  benchEditable: { ...AmplifyCore.state.DEFAULT_BENCH_EDITABLE },
  capabilities: { ...AmplifyCore.state.capabilities },
  caseVersions: [],
  itemSelectorProps: undefined,
  submitButtonConfig: {
    enabled: true,
    text: '',
    tooltip: '',
    icon: '',
    classNames: []
  },
  nextVersionButtonConfig: {
    enabled: false,
    text: '',
    tooltip: '',
    icon: ''
  },
  caseAccessState: CaseAccessState.NONE
};

const locatorFn = (activeItem: CMxAPI.CaseRunV2, item: string) => {
  const [, processInstanceGuid] = item.split(':');
  return processInstanceGuid === activeItem.process_instance_guid;
};

const getCaseVersionDate = (version: AmplifyAPI.CaseVersion): Date | string => {
  const dateVersion = new Date(version.processInstanceCreatedDate);
  if (isNaN(dateVersion.valueOf())) {
    return version.processInstanceCreatedDate;
  } else {
    return dateVersion;
  }
};

export type CodeBenchActionBarv2State = {
  disableCountDownTimer: boolean;
  revertDialogOpen: boolean;
};

type CodeBenchActionBarv2BaseProps = Accelerate.CodeBenchActionBarPropsV2 & {
  canSubmit?: boolean;
  isValidating?: boolean;
  auditActionBar?: React.ReactNode;
  guideline?: string | null;
  sampleReason?: string | null;
};

class CodeBenchActionBarv2Base extends React.Component<
  CodeBenchActionBarv2BaseProps,
  CodeBenchActionBarv2State
> {
  getViewOnlyReason = (
    capability: CMxCommonApp.Capability,
    benchEditable: Accelerate.BenchEditableState
  ) => {
    let displayMessage;
    let stringBundle = {};
    if (benchEditable.cancelled) {
      displayMessage = 'This case is cancelled.';
    } else if (benchEditable.submitted) {
      displayMessage = 'This case is submitted.';
    }
    displayMessage =
      (displayMessage ?? '') +
      'If you need permissions to edit this case, see your administrator.';

    if (
      conditionalUtils.capabilityValue(
        this.props.capabilities?.capabilities?.oldCaseLoadedAlert?.on
      )
    ) {
      displayMessage = 'You are viewing an non-codable version of this case';
    }

    stringBundle[CapabilityAvailabilityReason.CONFIGURATION] = displayMessage;
    let reasons = conditionalUtils.getStrings(capability.on, stringBundle);
    return joinStrings(reasons, '.');
  };

  constructor(props: CodeBenchActionBarv2BaseProps) {
    super(props);
    this.state = {
      disableCountDownTimer: false,
      revertDialogOpen: false
    };
    this.doClearInterval = this.doClearInterval.bind(this);
  }

  doClearInterval = () => {
    this.setState({
      disableCountDownTimer: true
    });
    setTimeout(() => {
      this.props.refreshHoldSubmission(this.props.activeItem);
    }, 1000);
  };

  render() {
    const props: Accelerate.CodeBenchActionBarPropsV2 = {
      ...DEFAULT_PROPS,
      ...this.props
    };
    const {
      cases,
      benchEditable,
      processInstanceGuids,
      loadState,
      activeItem,
      processInstance,
      capabilities,
      toggleHoldCaseModal,
      toggleExclusionCaseModal,
      showLateCaseClearModal,
      toggleSplitCaseModal,
      toggleChargeableCase,
      holdCase,
      holdCaseStatus,
      improvements,
      caseVersions,
      toggleSubmitOldCaseModal,
      getCaseVersions,
      cursorAPIContext,
      submitOldCaseModal,
      caseAccessTime,
      benchMode,
      handleSubmitCase,
      submitButtonConfig,
      onNextVersion,
      nextVersionButtonConfig,
      caseAccessState,
      workItem,
      activity,
      setActiveVerticalMiniTab,
      setTimelineFilter
    } = props;
    const hasMulti = cases?.length > 1;
    const feedbackCount = applyCQAFilters((activity ?? []) as Accelerate.ActivityItem[], 'CQA_FEEDBACK_COMMENT', activeItem?.grader_updated_date).length;
    const hasMultipleProcessInstances = processInstanceGuids?.length > 1;
    const showLoading = loadState.loading || loadState.initial;

    const isSubmitDisabled =
      benchMode === BenchMode.GRADE &&
      (!submitButtonConfig.enabled || this.props.canSubmit === false);

    const submitButtonStyle = [
      'chakra-primary-button',
      'submit-btn',
      'qa-submit-action-link',
      'action-bar-button',
      {
        disabled: isSubmitDisabled
      },
      ...(submitButtonConfig.classNames ?? [])
    ];

    const nextVersionButtonStyle = [
      'chakra-secondary-button',
      'prev-next-button',
      'action-bar-button',
      {
        disabled: !nextVersionButtonConfig.enabled
      },
      ...(nextVersionButtonConfig.classNames ?? [])
    ];

    let buttonCancel,
      buttonSubmit,
      processStatusDetail,
      processStatusStyleClass,
      processStatusTitle;

    const statusDisplay = caseUtils.deriveStatus(activeItem);

    function handleAction(buttonAction) {
      let loc = processInstanceGuids.findIndex((item) => {
        return item === buttonAction.item;
      });
      if (processInstanceGuids[loc]) {
        props.handleTraversal(processInstanceGuids[loc]);
      }
    }

    function handleCancel(event: React.MouseEvent) {
      event.preventDefault();
      props.onInvoicesCancel(processInstance);
    }

    function navigateWorklist(
      service_line: string,
      case_uid: string,
      processInstanceGuid: string
    ) {
      props.navigate(
        `/cmx/worklist/${service_line}/case/${case_uid}/case-run/${processInstanceGuid}`
      );
    }

    async function handleSubmit(
      event: React.MouseEvent,
      vwComment: string = null
    ) {
      event.preventDefault();
      handleSubmitCase(event, vwComment, isCodeableVersion);
    }
    // CQA-UI: handleBack handler
    function handleBack() {
      const path = props.backToWorklistPath?.startsWith('/')
        ? props.backToWorklistPath
        : `/cmx/${props.backToWorklistPath}`;
      props.navigate(path);
      props.showSideNavBar();
    }

    function handleNextPrev(
      event: React.MouseEvent,
      operation: string,
      hasAmplifyCaseRunV2Access: boolean
    ) {
      if(event) {
        event.preventDefault();
      }
      const cursorAPIPayload: CMxCommonApp.CursorAPIPayload = {
        operation,
        cursorAPIContext,
        hasAmplifyCaseRunV2Access
      };
      props.onCursorAction(cursorAPIPayload);
    }

    function handleNextVersion(
      event: React.MouseEvent,
      operation: string,
      hasAmplifyCaseRunV2Access: boolean
    ) {
      event.preventDefault();
      const cursorAPIPayload: CMxCommonApp.CursorAPIPayload = {
        operation,
        cursorAPIContext,
        hasAmplifyCaseRunV2Access
      };
      onNextVersion(cursorAPIPayload);
    }

    function handleOpenCase(processInstanceGuid: string, caseUid?: string) {
      if (processInstanceGuid) {
        const childWindowParams = getChildWindowParams(
          caseUid ?? activeItem?.case_uid,
          processInstanceGuid,
          activeItem?.service_line,
          launchAmplifyCase,
          activeItem?.case_id
        );
        props.openChildWindow(childWindowParams);
      }
    }

    if (processInstance) {
      processStatusDetail = benchEditableCore.determineProcessStatus(
        {
          isChargeable: processInstance.isChargeable,
          processInstanceStatus: processInstance.status,
          holdReason: holdCaseStatus.hold_reason,
          holdComment: holdCaseStatus.comment,
          qaStatus: processInstance.qaStatus,
          roles: this.props?.roles,
          cancelled: processInstance.cancelled,
          benchEditable,
          userContext: this.props?.userContext
        },
        true,
        activeItem?.parent_caserun_uid
      );
      processStatusStyleClass = benchEditableCore.fecthProcessStatusClasses(
        processStatusDetail,
        holdCaseStatus.hold_reason
      );
      processStatusTitle = processStatusDetail?.toLowerCase().includes('hold')
        ? holdCaseStatus.comment
        : '';
    }
    const styleClasses = [
      'code-bench-action-barv2',
      {
        'bench-pre-editable': benchEditable.waiting,
        'bench-loading': loadState.loading,
        'multiple-cases': hasMulti,
        'single-case': cases.length == 0
      }
    ];

    if (benchEditable.editable) {
      buttonCancel = (
        <button
          onClick={handleCancel}
          className="chakra-secondary-button qa-cancel-action-link action-bar-button">
          Void
        </button>
      );
      buttonSubmit = (
        <button
          onClick={handleSubmit}
          className={classnames(submitButtonStyle)}
          disabled={isSubmitDisabled}
          title={submitButtonConfig.tooltip}>
          {this.props.isValidating ? (
            <Spinner size="xs" speed="0.8s" mr={2} />
          ) : submitButtonConfig.icon ? (
            <CMXIcon
              svgIconName={submitButtonConfig.icon as any}
              svgIconSize="20"
            />
          ) : null}
          {benchMode == BenchMode.GRADE
            ? submitButtonConfig.text
            : (!!processInstance?.holdSubmission && !processInstance?.isCurrentInvoiceSubmitted ?'Check & Resubmit':'Check & Submit')}
        </button>
      );
    } else if (benchEditable.submitted) {
      buttonSubmit = (
        <button
          disabled
          className="chakra-primary-button submit-btn disabled action-bar-button">
          {benchMode == BenchMode.GRADE ? 'Submitted' : 'Checked & Submitted'}
        </button>
      );
    } else if (benchEditable.cancelled) {
      buttonCancel = (
        <button
          disabled
          className="chakra-secondary-button submit-btn disabled action-bar-button">
          Voided
        </button>
      );
    }

    const pageButtons = !hasMultipleProcessInstances ? null : (
      <PageButtons
        locatorFn={locatorFn}
        handleAction={handleAction}
        items={processInstanceGuids}
        activeItem={activeItem}
        key="page-buttons"
      />
    );

    let viewOnlyComponent = (
      <div className="view-only-section">
        <Conditional on={!capabilities.submitInProgress}>
          <span>View only Mode</span>
        </Conditional>
        <Conditional on={capabilities.submitInProgress}>
          <span>Submission in progress... </span>
        </Conditional>
        <strong
          title={this.getViewOnlyReason(
            capabilities.capabilities.viewOnlyCase,
            benchEditable
          )}>
          <BiomeIcon
            iconName={IconName.Info}
            iconClass="help-icon"
            iconSize="20"
          />
        </strong>
      </div>
    );
    const statusClasses = [
      'cmx-chip',
      props.className,
      'standalone-status',
      'description',
      statusDisplay.styleClass
    ];
    const versionClasses = [
      processStatusDetail ? 'withProcessStatus' : 'withOutProcessStatus',
      processStatusStyleClass && [
        ...processStatusStyleClass?.filter((item) => item !== 'process-status')
      ]
    ];

    /**
     * 1. improvement with provider query required and
     *  a. with no provider is counted as open improvement.
     *  b. with atleast one open provider reason is counted as open improvement.
     * 2. improvement with provider query
     *  a. not required is not an open improvement.
     *  b. required and had no provider reason as open, is not an open improvement.
     */
    const improvementCount = improvements?.improvements?.filter(
      (improvement) =>
        improvement?.provider_query &&
        (improvement?.providers?.length === 0 ||
          improvement?.providers?.some(
            (provider) =>
              provider?.provider_query_status === DocumentStatus.OPEN
          ))
    ).length;

    const currentVersionPosition =
      caseVersions?.findIndex(
        (caseVersion) =>
          caseVersion.processInstanceGuid === processInstance?.guid?.uuid
      ) ?? -1;

    const currentVersion =
      typeof currentVersionPosition === 'number' &&
      currentVersionPosition !== -1
        ? currentVersionPosition + 1
        : null;

    const newestVersionObject: AmplifyAPI.CaseVersion =
      caseVersions?.length > 0 ? caseVersions.slice().reverse().find(
        (caseVersion) => caseVersion.processInstanceStatus?.indexOf('REPLACED') < 0
      ) : null;

    const currentVersionObject: AmplifyAPI.CaseVersion = caseVersions?.find(
      (caseVersion) =>
        caseVersion.processInstanceGuid === processInstance?.guid?.uuid
    );

    const previousVersions = caseVersions?.filter(
      (_, index) => index < currentVersionPosition
    );

    let isCodeableVersion = true;

    if (
      newestVersionObject?.processInstanceGuid &&
      processInstance?.guid?.uuid
    ) {
      isCodeableVersion =
        newestVersionObject?.processInstanceGuid ===
          processInstance?.guid?.uuid &&
        (processInstance.status === commonEnums.AccelerateProcessStatus.NEW ||
          processInstance.status ===
            commonEnums.AccelerateProcessStatus.IN_PROGRESS ||
          ((processInstance?.qaStatus === commonEnums.ProcessQAStatus.IN_QA ||!!processInstance?.holdSubmission )&&
            processInstance.status ===
              commonEnums.AccelerateProcessStatus.SUBMITTED));
    }

    const PrefixTitle = () => {
      if (
        caseVersions.every(
          (caseVersion) =>
            caseVersion?.processInstanceStatus !==
            AccelerateProcessStatus.SUBMITTED
        ) &&
        previousVersions
          .filter(
            (caseVersion) =>
              caseVersion.processInstanceGuid !== processInstance?.guid?.uuid
          )
          .some(
            (caseVersion) =>
              caseVersion?.processInstanceStatus ===
                AccelerateProcessStatus.IN_PROGRESS ||
              caseVersion?.processInstanceStatus === AccelerateProcessStatus.NEW
          ) &&
        (currentVersionObject?.processInstanceStatus ===
          AccelerateProcessStatus.NEW ||
          currentVersionObject?.processInstanceStatus ===
            AccelerateProcessStatus.IN_PROGRESS)
      ) {
        return <div className="dot bg-new"></div>;
      }

      //@ts-ignore
      if (capabilities?.capabilities?.previousSentCaseAlert?.on.value) {
        return <div className="dot bg-update"></div>;
      }

      return null;
    };

    const timeDuration = cmxDateTime.calculateDurationDateFns(
      cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE),
      processInstance?.holdSubmissionDate
    );
    const timeDurationInSeconds =
      (timeDuration?.hours > 0 ? timeDuration?.hours : 0) * 60 +
      (timeDuration?.minutes > 0 ? timeDuration?.minutes : 0) * 60 +
      (timeDuration?.seconds > 0 ? timeDuration?.seconds : 0);

    const showHoldSubmissionTimer =
      processInstance?.holdSubmission && timeDurationInSeconds > 0;

    return (
      <div className={classnames(styleClasses)}>
        {
          <Conditional every={[toggleSplitCaseModal]}>
            <CaseComment
              title="Are you sure you want to split the case?"
              subtitle="This case will be considered non-chargeable, will be kept for reference, and charges are not submitted for billing."
              submitLabel="yes, split the case"
              mandatoryComment={false}
              actionOnlyNoComment={true}
              onCancel={props.hideSplitCaseModal}
              onSubmit={() => {
                props.splitCase(processInstance);
              }}
            />
          </Conditional>
        }

        {toggleHoldCaseModal && (
          <CaseHold
            title="Reason for holding a case"
            onHold={(holdReason: string, comment: string) =>
              holdCase({
                holdReason,
                comment,
                processInstance: processInstance
              })
            }
            onCancel={props.hideHoldCaseModal}
          />
        )}

        <Conditional {...capabilities.capabilities.toggleCaseComment}>
          <CaseComment
            title="Remove hold"
            submitLabel="Remove Hold"
            mandatoryComment={false}
            onCancel={props.hideCaseCommentModal}
            onSubmit={(comment: string) =>
              holdCase({
                holdReason: HoldReasonType.OFF_HOLD,
                comment,
                coding_manager_id: holdCaseStatus?.coding_manager_id,
                processInstance
              })
            }
          />
        </Conditional>

        {toggleExclusionCaseModal && (
          <CaseExclusion
            title={`Are you sure you want to ${
              processInstance?.isChargeable ? 'exclude' : 'include'
            } this case?`}
            isChargeable={processInstance?.isChargeable}
            onCaseExclusion={() =>
              toggleChargeableCase({
                serviceLine: processInstance.serviceLine,
                processInstanceGuid: processInstance.guid.uuid,
                isChargeable: !processInstance?.isChargeable
              })
            }
            onCancel={props.hideExclusionCaseModal}
          />
        )}

        {showLateCaseClearModal && (
          <CaseComment
            title="Are you sure you want to clear this late update notification. It cannot be undone?"
            submitLabel="yes, clear the notiifcation"
            mandatoryComment={false}
            actionOnlyNoComment={true}
            onCancel={() => props.toggleLateCaseClearModal(false)}
            onSubmit={() => {
              props.clearLateCaseNotification(processInstance);
            }}
          />
        )}

        {submitOldCaseModal && (
          <Modal
            title="More recent version found"
            onClose={toggleSubmitOldCaseModal}>
            <p className="submit-stale-case-warning">
              This version is no longer the most recent codable version of this case. It
              is locked from future editing. Please make your edits on the new
              version.
            </p>

            <div className="case-exclusion-buttons-group">
              <button
                onClick={toggleSubmitOldCaseModal}
                className="chakra-secondary-button">
                Cancel, keep me here
              </button>
              <button
                onClick={() =>
                  handleOpenCase(newestVersionObject?.processInstanceGuid)
                }
                className={classnames('chakra-primary-button')}>
                Ok, take me to the new case
              </button>
            </div>
          </Modal>
        )}

        {!loadState.loading && (
          <div className="action-bar">
            <div className="nav-bar-columns">
              <div className="nav-bar-column nav-bar-left-column align-left">
                {/*CQA-UI: add back to worklist buttom */}
                <button
                  onClick={handleBack}
                  className="chakra-secondary-button back-to-worklist-button action-bar-button">
                  <CMXIcon
                    svgIconName="BiomeLeftArrow"
                    svgIconSize="20"
                    svgIconClass="left-arrow"
                  />
                  Back to Worklist
                </button>
                <Conditional {...capabilities.capabilities.statusDescription}>
                  <div className={classnames(statusClasses)}>
                    <StatusDescription
                      caseRun={activeItem}
                      isPreGoLive={props.isPreGoLive}
                      secondaryIndicatorVisibility={false}
                    />
                  </div>
                </Conditional>
                {this.props.itemSelectorProps && (
                  <ItemSelect {...this.props.itemSelectorProps} />
                )}
                {benchMode === BenchMode.GRADE && (cursorAPIContext?.projectName || this.props.guideline || this.props.sampleReason) && (
                  <Popover placement="bottom-start" isLazy>
                    <PopoverTrigger>
                      <button className="chakra-secondary-button context-info-button action-bar-button">
                        <BiomeIcon iconName={IconName.Info} iconSize="16" />
                        Context
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="context-info-popover-content">
                      <PopoverArrow />
                      <PopoverBody>
                        <Flex direction="column" gap={2}>
                          {cursorAPIContext?.projectName && (
                            <Flex gap={2} align="baseline">
                              <span className="context-info-label">Project</span>
                              <span className="context-info-value">{cursorAPIContext.projectName}</span>
                            </Flex>
                          )}
                          {this.props.sampleReason && (
                            <Flex gap={2} align="baseline">
                              <span className="context-info-label">Purpose For Assessment</span>
                              <span className="context-info-value">{this.props.sampleReason}</span>
                            </Flex>
                          )}
                          {this.props.guideline && (
                            <Flex gap={2} align="baseline">
                              <span className="context-info-label">Guidelines</span>
                              <span className="context-info-value">{this.props.guideline}</span>
                            </Flex>
                          )}
                        </Flex>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                )}
                <Conditional every={[!!activeItem?.parent_case]}>
                  <MenuButton
                    label="Merged"
                    className="merged-case-menu-button">
                    {activeItem?.child_case_list?.map((childCase, index) => (
                      <MenuItem
                        key={index}
                        onSelect={() =>
                          handleOpenCase(
                            childCase.caserun_uid,
                            childCase.case_uid
                          )
                        }>
                        <div className="child-case-menu-item">
                          <span>{`Case #${childCase.case_id}`}</span>
                          <div className="menu-item-tags">
                            <Conditional every={[childCase.type === 'primary']}>
                              <span className="merge-originating-case primary">
                                Primary
                              </span>
                            </Conditional>
                            <Conditional
                              every={[
                                processInstance?.status !==
                                  AccelerateProcessStatus.REMOVED
                              ]}>
                              <span className="merge-originating-case inactive">
                                Inactive
                              </span>
                            </Conditional>
                          </div>
                        </div>
                      </MenuItem>
                    ))}
                  </MenuButton>
                </Conditional>
                {/**delete**/}
                <Conditional {...capabilities.capabilities.versionProcess}>
                  <div className="version-process">
                    {caseVersions?.length > 0 && (
                      <MenuButton
                        label={
                          currentVersion ? `Version ${currentVersion}` : ''
                        }
                        disabled={caseVersions.length === 1}
                        prefixTitle={<PrefixTitle />}
                        className={classnames(versionClasses)}>
                        {caseVersions.map((caseVersion, caseVersionIndex) => (
                          <MenuItem
                            key={caseVersionIndex}
                            onSelect={() =>
                              handleOpenCase(caseVersion.processInstanceGuid)
                            }>
                            <div className="version-item">
                              <p>Version {caseVersionIndex + 1}</p>
                              {caseVersion.processInstanceCreatedDate && (
                                <p>
                                  Filing date:{' '}
                                  {cmxDateTime.format(
                                    getCaseVersionDate(caseVersion),
                                    cmxDateTime.FORMATS.DATE_TIME_UI
                                  )}
                                </p>
                              )}
                              {caseVersion.processInstanceGuid !==
                                processInstance?.guid?.uuid &&
                                (caseVersion.processInstanceStatus ===
                                  AccelerateProcessStatus.IN_PROGRESS ||
                                  caseVersion.processInstanceStatus ===
                                    AccelerateProcessStatus.NEW) && (
                                  <p>Coding Started</p>
                                )}
                            </div>
                          </MenuItem>
                        ))}
                      </MenuButton>
                    )}
                    {(!showHoldSubmissionTimer ||
                      this.state.disableCountDownTimer) &&
                      processStatusDetail && (
                        <div
                          className={`${classnames(
                            processStatusStyleClass
                          )} process-col`}
                          title={processStatusTitle}>
                          {processStatusDetail}
                        </div>
                      )}
                    {showHoldSubmissionTimer &&
                      !this.state.disableCountDownTimer && (
                        <Flex
                          className={`${classnames(
                            processStatusStyleClass
                          )} process-col`}>
                          Pending (
                          <CountdownTimer
                            timeDurationLeft={timeDurationInSeconds}
                            doClearInterval={this.doClearInterval}/> &nbsp; remaining)
                        </Flex>
                      )}
                  </div>
                </Conditional>

                {improvementCount !== 0 && (
                  <div
                    className="cmx-chip improvement-reasons-needed"
                    title={`${improvementCount} improvement ${pluralize(
                      'reason',
                      improvementCount
                    )} need to be resolved`}>
                    <CMXIcon
                      svgIconName="BiomeReports"
                      svgIconSize="12"
                      svgTitleText={`${improvementCount} improvement ${pluralize(
                        'reason',
                        improvementCount
                      )} need to be resolved`}
                    />
                    {improvementCount}
                  </div>
                )}
                {this.props.conflictingUser && (
                  <Tooltip label={this.props.conflictingUser} placement="right">
                    <Avatar
                      name={this.props.conflictingUser}
                      bgColor="#948381"
                      color="#FFFFFF"
                      size="sm"
                    />
                  </Tooltip>
                )}
              </div>
              <div className="nav-bar-column nav-bar-right-column charge-submission-column action-column action-bar-right-column">
                <div style={{ display: "flex", alignItems: "center", width: "40px" }}>
                  <Conditional
                    every={[
                      benchMode === BenchMode.GRADE,
                      [IN_CQA, ASSIGNED_TO_ME].includes(cursorAPIContext?.worklistContext?.selectedStatus),
                      feedbackCount > 0
                    ]}
                  >
                      <div style={{ position: "relative" }}>
                        <button
                          data-testid="cqa-feedback-button"
                          onClick={() => {
                            setActiveVerticalMiniTab?.(commonEnums.MiniTabLabels.ACTIVITY_LABEL);
                            setTimelineFilter?.('feedback');
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M20 2H4C2.9 2 2.01 2.9 2.01 4L2 22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                              fill="none"
                              stroke="#E5927D"
                              strokeWidth="1.5"
                            />
                            <path
                              d="M13 14H11V12H13V14ZM13 10H11V6H13V10Z"
                              fill="#E5927D"
                            />
                          </svg>
                            <Badge
                              position="absolute"
                              top="-3"
                              right="-3"
                              color="white"
                              backgroundColor={"#E50000"}
                              borderRadius="full"
                              minW="5"
                              h="5"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                              px="1"
                            >
                              {feedbackCount}
                            </Badge>
                        </button>
                      </div>
                  </Conditional>
                </div>
                  <Conditional
                    every={[
                      benchMode === BenchMode.GRADE,
                      [CQA_COMPLETE, CQA_APPROVED].includes(cursorAPIContext?.worklistContext?.selectedStatus),
                      this.props?.roles?.includes("cqa_manager")
                    ]}
                  >
                    <div>
                      <button
                        className={classnames(['chakra-secondary-button', 'prev-next-button', 'action-bar-button',])}
                        onClick={() => this.setState({ revertDialogOpen: true })}
                      >
                        {<UndoIcon />}
                        Revert
                      </button>
                    </div>
                  </Conditional>
                <Conditional {...capabilities.capabilities.pagination}>
                  {pageButtons}
                </Conditional>

                {this.props.auditActionBar}

                <Conditional
                  {...capabilities.capabilities
                    .actionBarSelectMultipleWorkitems}
                  every={[benchMode === BenchMode.GRADE]}>
                  <button
                    onClick={(ev) =>
                      handleNextVersion(
                        ev,
                        CursorOperation.NEXT,
                        benchMode == BenchMode.GRADE ? false : true
                      )
                    }
                    disabled={!nextVersionButtonConfig.enabled}
                    className={classnames(nextVersionButtonStyle)}>
                    {nextVersionButtonConfig.text}
                    <CMXIcon svgIconName="BiomeRightArrow" svgIconSize="20" />
                  </button>
                </Conditional>
                {!showLoading && (
                  <Conditional {...capabilities.capabilities.servicedeskAction}>
                    <button
                      onClick={(ev) =>
                        handleNextPrev(
                          ev,
                          CursorOperation.NEXT,
                          benchMode == BenchMode.GRADE ? false : true
                        )
                      }
                      className="chakra-secondary-button prev-next-button action-bar-button">
                      Next
                      <CMXIcon svgIconName="BiomeRightArrow" svgIconSize="20" />
                    </button>
                  </Conditional>
                )}

                <Conditional
                  every={[
                    conditionalUtils.capabilityValue(
                      capabilities.capabilities?.actionMenu?.on
                    ),
                    isCaseAvailable(caseAccessState)
                  ]}>
                  <MenuButton label="Actions">
                    <Conditional
                      {...capabilities.capabilities.actionMenuCancelButton}
                      displayMode="flex">
                      <MenuItem onSelect={handleCancel}>Void Case</MenuItem>
                    </Conditional>

                    <Conditional {...capabilities.capabilities.holdCase}>
                      <MenuItem onSelect={props.showHoldCaseModal}>
                        Hold Case
                      </MenuItem>
                    </Conditional>
                    <Conditional {...capabilities.capabilities.removeHoldCase}>
                      <MenuItem onSelect={props.showCaseCommentModal}>
                        Remove Hold
                      </MenuItem>
                    </Conditional>
                    <Conditional {...capabilities.capabilities.createCase}>
                      <MenuItem
                        onSelect={() => props.navigateCreateCase(activeItem)}>
                        Create Case from Encounter/HAR
                      </MenuItem>
                    </Conditional>
                    <Conditional
                      {...capabilities.capabilities.toggleChargeableCase}>
                      <MenuItem onSelect={props.showExclusionCaseModal}>
                        {processInstance?.isChargeable
                          ? 'Exclude Case'
                          : 'Include Case'}
                      </MenuItem>
                    </Conditional>
                    <Conditional {...capabilities.capabilities.mergeCapability}>
                      <MenuItem
                        onSelect={() =>
                          props.showMergeCasesModal({
                            caseRun: activeItem,
                            cursorAPIContext
                          })
                        }>
                        Merge Case
                      </MenuItem>
                    </Conditional>
                    <Conditional {...capabilities.capabilities.splitCapability}>
                      <MenuItem onSelect={() => props.showSplitCaseModal()}>
                        Split Case
                      </MenuItem>
                    </Conditional>
                    <Conditional
                      {...capabilities.capabilities.lateCaseClearNotification}>
                      <MenuItem
                        onSelect={() => props.toggleLateCaseClearModal(true)}>
                        Clear late update notification
                      </MenuItem>
                    </Conditional>
                  </MenuButton>
                </Conditional>

                <Conditional {...capabilities.capabilities.generalCancelButton}>
                  {buttonCancel}
                </Conditional>

                <Conditional {...capabilities.capabilities.submitCase}>
                  {buttonSubmit}
                </Conditional>
                <Conditional {...capabilities.capabilities.viewOnlyCase}>
                  {viewOnlyComponent}
                </Conditional>
                <Conditional
                  every={[!!workItem?.late_case_update && !workItem?.dismissed]}>
                  <button
                    onClick={() => props.updateWorkItemDismissed?.(workItem?.work_item_case_id || '', true)}
                    className="chakra-primary-button dismiss-late-notification-button action-bar-button"
                    style={{ backgroundColor: '#5ba37a', color: 'white !important' }}>
                    <CMXIcon
                      svgIconName="RadioEmpty20"
                      svgIconSize="20"
                    />
                    Clear late update notification
                  </button>
                </Conditional>
                <Conditional
                  every={[!!workItem?.late_case_update && workItem?.dismissed]}>
                  <button
                    className="chakra-secondary-button dismiss-late-notification-button action-bar-button"
                    onClick={() => props.updateWorkItemDismissed?.(workItem?.work_item_case_id || '', false)}
                    style={{ borderColor: '#e5927d', color: '#e5927d' }}>
                    <CMXIcon
                      svgIconName="BiomeCheckSVG20"
                      svgIconSize="20"
                      svgIconClass="left-arrow"
                    />
                    Late update notification cleared
                  </button>
                </Conditional>
              </div>
            </div>

          </div>
        )}
        <Conditional
          every={[
            benchMode === BenchMode.GRADE,
            [CQA_COMPLETE, CQA_APPROVED].includes(cursorAPIContext?.worklistContext?.selectedStatus),
          ]}
        >
          <RevertApproval
            open={this.state.revertDialogOpen}
            selected={[{
              sd_case_id: cursorAPIContext?.workitemId,
              qa_status: cursorAPIContext?.worklistContext?.selectedStatus
            }]}
            cqaWorkitemStatus={cursorAPIContext?.worklistContext?.selectedStatus}
            handleClose={() => this.setState({ revertDialogOpen: false })}
            handleAfterRevert={() =>
              handleNextPrev(
                null,
                CursorOperation.NEXT,
                benchMode == BenchMode.GRADE ? false : true
              )}
          />
        </Conditional>
      </div>
    );
  }
}

const CQA_AUDIT_STATUS = 'CQA_AUDIT';

const CodeBenchActionBarv2: React.FC<Accelerate.CodeBenchActionBarPropsV2> = (props) => {
  const gradeBenchPanelState = useSelector(
    (state: CMx.ApplicationState) => state.gradeBenchPanel
  );

  React.useEffect(() => {
    const handleCloseGradingScreen = () => {
      const backPath = props.backToWorklistPath;
      if (backPath && props.navigate) {
        const path = backPath.startsWith('/') ? backPath : `/cmx/${backPath}`;
        props.navigate(path);
        props.showSideNavBar?.();
      }
    };
    window.addEventListener('close-grading-screen', handleCloseGradingScreen);
    return () => window.removeEventListener('close-grading-screen', handleCloseGradingScreen);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const project_configuration = gradeBenchPanelState?.qaRule?.data?.project_configuration;
  const usesValidatedWorkflow = project_configuration?.rubricVersionId != null;
  const validationResult = gradeBenchPanelState?.validationResult;
  const invoices = gradeBenchPanelState?.invoices;
  const guideline = gradeBenchPanelState?.qaRule?.data?.guideline ?? null;
  const sampleReason = gradeBenchPanelState?.qaRule?.data?.sample_reason ?? null;

  const itemStatus = invoices && invoices.length > 0 ? invoices[0].status : undefined;
  const isAuditItem =
    props.cursorAPIContext?.worklistContext?.selectedStatus === CQA_AUDIT_STATUS ||
    itemStatus === CQA_AUDIT_STATUS ||
    itemStatus === 'CQA_AUDIT_COMPLETE';
  const auditRuleId: string | undefined =
    props.cursorAPIContext?.worklistContext?.selectedProjectId;

  const [auditClosed, setAuditClosed] = React.useState(false);

  const handleAuditClosed = React.useCallback(
    (result: { status: string; failure_mode?: string; items_reviewed?: number } | null) => {
      setAuditClosed(true);
      if (props.onAuditEnded) {
        props.onAuditEnded(result);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  let auditSubmitButtonConfig = props.submitButtonConfig ?? DEFAULT_PROPS.submitButtonConfig!;
  if (isAuditItem && !auditClosed) {
    auditSubmitButtonConfig = {
      ...props.submitButtonConfig,
      text: 'Submit',
      enabled: true,
    };
  }

  let canSubmit = true;

  if (usesValidatedWorkflow) {
    canSubmit = validationResult?.isValid === true;
  } else if (project_configuration && invoices) {
    canSubmit = isCQAWorkItemEvaluatedLegacy(project_configuration, invoices);
  }

  const auditActionBar =
    isAuditItem && !auditClosed && auditRuleId ? (
      <AuditActionBar ruleId={auditRuleId} onAuditClosed={handleAuditClosed} />
    ) : null;

  return (
    <CodeBenchActionBarv2Base
      {...props}
      submitButtonConfig={auditSubmitButtonConfig}
      canSubmit={canSubmit}
      auditActionBar={auditActionBar}
      guideline={guideline}
      sampleReason={sampleReason}
    />
  );
};

export { CodeBenchActionBarv2 };
